import { useState } from "react";
import capi, { setCustomerSession } from "@/lib/customerApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Phone, Mail, Instagram, PartyPopper, MessageSquareText } from "lucide-react";

function errText(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
}

// Flow: form (requirement) -> try contact. If backend needs mobile (401/403) -> mobile -> otp -> contact.
// A user whose mobile is already verified (via Google or a prior OTP session) skips OTP entirely.
export default function ContactVendorDialog({ open, onOpenChange, vendorId, vendorName, city, category, onLead }) {
  const [step, setStep] = useState("form");
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [requirement, setRequirement] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState(null);

  const reset = () => { setStep("form"); setCode(""); setContact(null); setRequirement(""); };

  const doContact = async () => {
    const { data } = await capi.post(`/partner/public/vendor/${vendorId}/contact`, {
      project_requirement: requirement, city: city || "", category: category || "",
    });
    setContact(data.contact); setStep("done");
    onLead && onLead(data);
    toast.success("Your request has been sent to the partner.");
  };

  const submitForm = async () => {
    if (!requirement.trim()) { toast.error("Please describe your requirement."); return; }
    setBusy(true);
    try {
      await doContact(); // works if mobile already verified (Google or OTP session)
    } catch (e) {
      const s = e.response?.status;
      if (s === 401 || s === 403) {
        setStep("mobile"); // needs one-time mobile verification
      } else {
        toast.error(errText(e.response?.data?.detail));
      }
    } finally { setBusy(false); }
  };

  const sendOtp = async () => {
    if (mobile.trim().length !== 10) { toast.error("Enter a valid 10-digit mobile."); return; }
    setBusy(true);
    try {
      await capi.post("/customer/otp/request", { mobile: mobile.trim() });
      setStep("otp"); toast.success("OTP sent to your mobile");
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (code.trim().length < 4) { toast.error("Enter the OTP"); return; }
    setBusy(true);
    try {
      const { data } = await capi.post("/customer/otp/verify", { mobile: mobile.trim(), code: code.trim(), name: name.trim() });
      setCustomerSession(data.token, data.customer);
      await doContact();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="contact-vendor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Contact {vendorName}</DialogTitle>
          <DialogDescription>
            {step === "done" ? "You're connected — here are the partner's contact details." :
             step === "form" ? "Send your requirement to this verified partner." :
             "Verify your mobile once — you won't be asked again."}
          </DialogDescription>
        </DialogHeader>

        {step === "form" && (
          <div className="space-y-4">
            <div>
              <Label>Project Requirement</Label>
              <Textarea data-testid="contact-requirement" className="mt-1.5 rounded-xl" value={requirement} onChange={(e) => setRequirement(e.target.value)} placeholder="e.g. Turnkey construction for a 2400 sqft plot in Noida, budget ~₹60L" />
            </div>
            <Button data-testid="contact-submit" onClick={submitForm} disabled={busy} className="w-full h-11 rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MessageSquareText className="h-4 w-4 mr-2" /> Send & Get Contact</>}
            </Button>
            <p className="text-xs text-slate-400 text-center">We never share your number publicly. It's used only to connect you with partners.</p>
          </div>
        )}

        {step === "mobile" && (
          <div className="space-y-4">
            <div><Label>Your Name</Label><Input data-testid="contact-name" className="mt-1.5 rounded-xl" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" /></div>
            <div><Label>Mobile Number</Label><Input data-testid="contact-mobile" className="mt-1.5 rounded-xl" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" onKeyDown={(e) => e.key === "Enter" && sendOtp()} /></div>
            <Button data-testid="contact-sendotp" onClick={sendOtp} disabled={busy} className="w-full h-11 rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-2" /> Send OTP</>}
            </Button>
          </div>
        )}

        {step === "otp" && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Enter the 6-digit OTP sent to <b>{mobile}</b>.</p>
            <Input data-testid="contact-otp" className="rounded-xl text-center tracking-[0.4em] text-lg" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" onKeyDown={(e) => e.key === "Enter" && verifyOtp()} />
            <Button data-testid="contact-verify" onClick={verifyOtp} disabled={busy} className="w-full h-11 rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Contact"}
            </Button>
            <button onClick={() => setStep("mobile")} className="w-full text-xs text-slate-500 hover:text-[#0E9AC8]">← Change number</button>
          </div>
        )}

        {step === "done" && contact && (
          <div className="space-y-4" data-testid="contact-revealed">
            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-emerald-800 text-sm">
              <PartyPopper className="h-4 w-4" /> Your request was sent to the partner.
            </div>
            <div className="space-y-2">
              {contact.phone && <a href={`tel:${contact.phone}`} data-testid="revealed-phone" className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-[#0E9AC8]"><Phone className="h-4 w-4 text-[#0E9AC8]" /><span className="font-medium">{contact.phone}</span></a>}
              {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-[#0E9AC8]"><Mail className="h-4 w-4 text-[#0E9AC8]" /><span className="font-medium break-all">{contact.email}</span></a>}
              {contact.instagram && <a href={contact.instagram.startsWith("http") ? contact.instagram : `https://${contact.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-[#0E9AC8]"><Instagram className="h-4 w-4 text-[#0E9AC8]" /><span className="font-medium break-all">{contact.instagram}</span></a>}
            </div>
            <Button onClick={() => { onOpenChange(false); reset(); }} className="w-full h-11 rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4]">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
